import { AnimatePresence } from 'framer-motion';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { scrollBarStyles } from '@/utils/theme';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  ImagesBox,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import GeneratedImages from './GeneratedImages';
import NewUploadedImageRow from './NewUploadedImageRow';
import VariationsStyles from './VariationsStyles';

export interface ImageVariation {
  leftImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightImageSrc: string;
  rightWatermarkTag: string;
  rightWatermarkPosition: string;
  leftText: string;
  rightText: string;
  leftTextPositon: string;
  leftQuotes: boolean;
  leftBreakes: boolean;
  rightTextPositon: string;
  rightQuotes: boolean;
  rightBreakes: boolean;
  divider: string;
  topText: string;
  topTextBrakes?: boolean;
  watermark: boolean;
}

const TextAreaBox = styled.div`
  padding: 24px;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  flex-grow: 1;
  align-content: start;
`;

const TopTextAreaBox = styled(TextAreaBox)`
  max-height: 246px;
  overflow-y: auto;
  ${scrollBarStyles}
`;

const TextItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LabelHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CounterBadge = styled(Text)`
  background: ${({ theme }) => theme.colors.purple};
  width: 20px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AdditionalBox = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const StyledRemoveButton = styled(RemoveButton)`
  background: transparent;
  height: auto;
  padding: 0;
  margin-bottom: 8px;
  text-decoration: underline;
  transition: all 0.2s;
  &:hover {
    border-color: transparent;
    opacity: 0.8;
  }
`;

const TopTextArea = styled(Textarea)`
  height: 68px;
`;

const ImageGrid = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const TextRight = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 24px;
`;

const AddMoreHolder = styled.div`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const StyeldAddMoreButton = styled(AddMoreButton)`
  margin-left: auto;
`;

type TopTextItem = { text: string; brakes: boolean };

interface Props {
  inArticle?: boolean;
}

const VariationTwoImagesPlusTextTrivia: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [rightImages, setRightImages] = useState<ImageData[]>([]);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  const [showLeftImagesModal, setShowLeftImagesModal] = useState(false);
  const [showRightImagesModal, setShowRightImagesModal] = useState(false);
  const [watermark, setWatermark] = useState(true);
  const [multipleVariations, setMultipleVariations] = useState(false);
  const [topTexts, setTopTexts] = useState<TopTextItem[]>([
    { text: '', brakes: false },
  ]);

  const renderVariationStyles = () => {
    return <VariationsStyles />;
  };

  const generateVariations = useCallback(() => {
    if (!leftImages.length || !rightImages.length) return;

    const headingItems = topTexts.filter(Boolean);

    const basePairs = leftImages.flatMap((leftImage) =>
      rightImages.map((rightImage) => ({
        leftWatermarkTag: getWatermarkTag(leftImage),
        leftWatermarkPosition: getWatermarkPosition(leftImage),
        leftImageSrc:
          leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
        rightWatermarkTag: getWatermarkTag(rightImage),
        rightWatermarkPosition: getWatermarkPosition(rightImage),
        rightImageSrc:
          rightImage.croppedImage ?? URL.createObjectURL(rightImage.image),
        leftText: leftImage.text,
        rightText: rightImage.text,
        leftTextPositon: leftImage.textPosition,
        leftQuotes: leftImage.quotes,
        leftBreakes: leftImage.brakes,
        rightTextPositon: rightImage.textPosition,
        rightQuotes: rightImage.quotes,
        rightBreakes: rightImage.brakes,
        divider: leftImage.divider,
      })),
    );

    const newVariations: ImageVariation[] = basePairs.flatMap((pair) =>
      (headingItems.length ? headingItems : [{ text: '', brakes: false }]).map(
        (h) => ({
          ...pair,
          topText: h?.text ?? '',
          topTextBrakes: !!h?.brakes,
          watermark: watermark,
        }),
      ),
    );

    setVariations(newVariations);
  }, [leftImages, rightImages, topTexts, watermark]);

  const renderLeftImages = leftImages.map((image, index) => (
    <NewUploadedImageRow
      settings
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
      type="left"
    />
  ));

  const renderRightImages = rightImages.map((image, index) => (
    <NewUploadedImageRow
      settings
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setRightImages}
      type="right"
    />
  ));

  const addTopText = () =>
    setTopTexts((prev) => [...prev, { text: '', brakes: false }]);

  const updateTopText = (idx: number, value: string) =>
    setTopTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, text: value } : t)),
    );

  const toggleTopTextBrakes = (idx: number) =>
    setTopTexts((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, brakes: !t.brakes } : t)),
    );

  const removeTopText = (idx: number) =>
    setTopTexts((prev) => prev.filter((_, i) => i !== idx));

  const onReset = () => {
    setRightImages([]);
    setLeftImages([]);
    setVariations([]);
    setImages([]);
    setTopTexts([{ text: '', brakes: false }]);
    setWatermark(true);
  };

  const isResetDisabled =
    !leftImages?.length &&
    !rightImages?.length &&
    !topTexts.length &&
    !watermark;

  return (
    <>
      {renderVariationStyles()}
      <Box>
        <Header>
          <div>
            <Heading $type="button">2 Img + Text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Top heading text, followed by two side-by-side cover images each
              with a text block near the bottom
            </Text>
          </div>
          <HeaderButtons>
            <FancySwitch
              isActive={multipleVariations}
              onClick={() => setMultipleVariations((prev) => !prev)}
              text="Multiple Variations:"
            />
            <ResetButton
              color="red"
              onClick={onReset}
              disabled={isResetDisabled}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <div>
            <TopTextAreaBox>
              {topTexts.map((item, idx) => (
                <TextItem key={idx}>
                  <div>
                    <LabelHolder>
                      <LabelText $type="smallHeadline">
                        Heading Text
                        {topTexts.length > 1 && (
                          <CounterBadge $type="extraSmall" $color="white">
                            {idx + 1}
                          </CounterBadge>
                        )}
                      </LabelText>
                      {topTexts.length > 1 && (
                        <StyledRemoveButton
                          color="red"
                          onClick={() => removeTopText(idx)}
                        >
                          Remove
                        </StyledRemoveButton>
                      )}
                    </LabelHolder>
                    <TopTextArea
                      onChange={(e) => updateTopText(idx, e.target.value)}
                      value={item.text}
                      placeholder="Add text"
                    />
                  </div>
                  <TextRight>
                    <Checkbox
                      checked={!!item.brakes}
                      onChange={() => toggleTopTextBrakes(idx)}
                    >
                      Custom brakes
                    </Checkbox>
                  </TextRight>
                </TextItem>
              ))}
            </TopTextAreaBox>
            {multipleVariations && (
              <AddMoreHolder>
                {topTexts.length > 1 && (
                  <AttachedCountBadge count={topTexts.length} text="Headings" />
                )}
                <StyeldAddMoreButton color="colorized" onClick={addTopText}>
                  Add heading text
                </StyeldAddMoreButton>
              </AddMoreHolder>
            )}
            <ImageGrid>
              <ImagesBox $hasImages={!!leftImages?.length}>
                <LabelText $type="smallHeadline">
                  Left Cover Image & Text
                </LabelText>
                {!!leftImages?.length ? (
                  <>
                    <UploadedImages>{renderLeftImages}</UploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={leftImages.length} />
                      <AddMoreButton
                        onClick={() => setShowLeftImagesModal(true)}
                        color="colorized"
                      >
                        Add more left images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <UploadMultipleImages
                    coverIndex={1}
                    setImages={setLeftImages}
                  />
                )}
              </ImagesBox>
              <ImagesBox $hasImages={!!rightImages?.length}>
                <LabelText $type="smallHeadline">
                  Right Cover Image & Text
                </LabelText>
                {!!rightImages?.length ? (
                  <>
                    <UploadedImages>{renderRightImages}</UploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={rightImages.length} />
                      <AddMoreButton
                        onClick={() => setShowRightImagesModal(true)}
                        color="colorized"
                      >
                        Add more right images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <UploadMultipleImages
                    coverIndex={2}
                    setImages={setRightImages}
                  />
                )}
              </ImagesBox>
            </ImageGrid>
            <AdditionalBox>
              <Text $type="smallHeadline" $color="gray">
                Additional:
              </Text>
              <Checkbox
                checked={!!watermark}
                onChange={() => setWatermark((prev) => !prev)}
              >
                Bored Panda Watermark
              </Checkbox>
            </AdditionalBox>
          </div>
        )}
      </Box>
      <Filler />
      {showContent && (
        <ButtonHolder>
          <Button
            disabled={!leftImages?.length || !rightImages?.length}
            onClick={generateVariations}
          >
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showLeftImagesModal && (
          <MoreImagesModal
            onClose={() => setShowLeftImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
        {!!showRightImagesModal && (
          <MoreImagesModal
            onClose={() => setShowRightImagesModal(false)}
            setImages={setRightImages}
            coverIndex={2}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationTwoImagesPlusTextTrivia;
